import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { logResultTrackEvent } from '../payment-result/utils/index.js';
import usePaymentPoll, { POLL_INTERVAL_DELAY } from './usePaymentPoll.js';
import constants, { FETCH_TYPE_ASYNCH } from '../common/lib/utils/constants.js';

/**
 * @param {PollData} pollData
 * @param {function} onDepositSuccessShop
 * @returns {{pollResult: PollResult, paymentState: string}}
 */
function useResultPagePoll(pollData, onDepositSuccessShop) {
	const dispatch = useDispatch();
	const [paymentState, setPaymentState] = useState(null);
	const [isPollTimeOver, setPollTimeOver] = useState(false);
	const { pollResult, startPolling, stopPolling } = usePaymentPoll(pollData);

	useEffect(() => {
		let pollCounts = 1; // default for synch fetch type
		let timeoutId = null;

		if (pollData.totalTimeToWaitInMs) {
			// get count from totalTimeToWaitInMs property, like 15 000ms / 3000ms = 5
			pollCounts = Math.floor(pollData.totalTimeToWaitInMs / POLL_INTERVAL_DELAY);
		} else if (pollData.fetch_type === FETCH_TYPE_ASYNCH) {
			pollCounts = pollData.playerRedirectedToFailUrl ? 3 : 5;
		}
		timeoutId = setTimeout(
			() => {
				stopPolling();
				setPollTimeOver(true);
			},
			pollCounts * POLL_INTERVAL_DELAY + POLL_INTERVAL_DELAY
		);

		startPolling();

		return () => {
			stopPolling();
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
		// eslint-disable-next-line
	}, []);

	// Handling onDepositSuccessShop function
	useEffect(() => {
		if (paymentState && pollResult && onDepositSuccessShop) {
			if (pollResult?.bonusId > 0) {
				onDepositSuccessShop(pollResult?.bonusId, dispatch);
			}
		}
	}, [paymentState, pollResult, onDepositSuccessShop, dispatch]);

	useEffect(() => {
		const resultData = pollResult ?? {};
		logResultTrackEvent({ accHistRef: pollData?.accHistRef, ...resultData });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pollResult]);

	const handlePaymentResult = useCallback(() => {
		// eslint-disable-next-line camelcase
		const { handlePaymentResultCallback, fetch_type } = pollData;
		const { willStayInPending, state } = pollResult;

		// eslint-disable-next-line camelcase
		if (fetch_type === constants.WITHDRAW || handlePaymentResultCallback === 'pendingAfterSingleCallback') {
			if (state === constants.PENDING || state === constants.SUCCESS) {
				setPaymentState(state);
			} else {
				setPaymentState(constants.CANCELLED);
			}
			stopPolling();
		} else {
			switch (state) {
				case constants.CANCELLED:
				case constants.SUCCESS:
					setPaymentState(state);
					stopPolling();
					break;
				case constants.PENDING:
					if (willStayInPending || isPollTimeOver) {
						setPaymentState(constants.PENDING);
						stopPolling();
					}
					break;
				case constants.NOT_FOUND:
					if (isPollTimeOver) {
						setPaymentState(constants.CANCELLED);
						stopPolling();
					}
					break;
				case constants.ERROR:
					setPaymentState(constants.ERROR);
					stopPolling();
					break;
				default:
					break;
			}
		}
		// eslint-disable-next-line
	}, [pollData, pollResult, isPollTimeOver]);

	// Handling changes on each pollResult and isPollTimeOver changes
	useEffect(() => {
		if (pollResult) {
			handlePaymentResult(pollResult);
		}
		// eslint-disable-next-line
	}, [pollResult, isPollTimeOver]);

	return { pollResult, paymentState };
}

export default useResultPagePoll;
